import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { Button, Notification, Typography, Spacer } from '@telus-uds/components-web';

import StatusError from '@telus-uds/palette-allium/build/web/icons/StatusError';
import { addLocale } from '../misc';
import LoginShema from './schema';
import request from '../utils/request';
import ValidateLoader from '../UI/validateLoader';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { CONSTANT } from '../utils/constant';
import { useLocaleContext } from '../context/LocaleContext';

import '../../assets/css/login.css';
import '../../assets/css/login_media_queries.css';
import { directAccessConfigs } from '../utils/directAccessConfig';

const CustomLogin = ({ history }) => {
  const [loading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const locale = useLocaleContext();
  const addLocaleToUrl = addLocale(locale);
  const localizedStrings = useLocalizedStringsContext();
  const {
    USERS: {
      USER_AUTHENTICATION: {
        labels: { PLACEHOLDER_EMAIL, USER_LOGIN_PASSWORD, USER_VERIFY_MESSAGE },
        ERRORS_MESSAGES: { GENERIC_ERROR_HEADING, GENERIC_ERROR_MESSAGE }
      }
    }
  } = localizedStrings;

  const {
    KEYS: { ROLES }
  } = CONSTANT;

  const getRedirectUrl = role => {
    let redirectUrl = '/';
    switch (role) {
      case ROLES.SOV_ADMIN:
      case ROLES.SOV_AGENT: {
        redirectUrl = '/orders';
        break;
      }
      case ROLES.PRODUCT_TEAM: {
        redirectUrl = '/quote-approval';
        break;
      }
      default:
        break;
    }
    return redirectUrl;
  };

  const getUserProfile = () => {
    return request.get('/users/profile');
  };

  const loginRequest = values => {
    setLoading(true);
    const { email, password } = values;
    request
      .post('/users/login', { email, password })
      .then(response => {
        const { twoFactorAuthRequired } = response && response.data;
        /**
         * If 2FA is required
         */
        if (twoFactorAuthRequired) {
          history.push(addLocaleToUrl('/two-factor/verify'));
          localStorage.setItem('userEmail', email);
        } else {
          /**
           * If 2FA is not required
           */
          const { token, changePassword } = response && response.data;
          localStorage.setItem('isExpired', 'false');
          request.setToken(token);
          request.setPasswordUpdate(changePassword);
          getUserProfile().then(result => {
            const { data = {} } = result;
            request.setProfile(data);
            const userUrl = addLocaleToUrl(getRedirectUrl(data.role.name));
            const redirectAfterLoginUrl = localStorage.getItem(
              'redirectAfterLogin'
            );
            const isRedirectValid = localStorage.getItem('isRedirectValid');
            const allWebformUrls = Object.values(
              directAccessConfigs
            ).flatMap(config => Object.values(config.webformUrls));
            localStorage.removeItem('redirectAfterLogin');
            localStorage.setItem('isRedirectValid', false);

            history.push(
              allWebformUrls.includes(redirectAfterLoginUrl) && isRedirectValid
                ? redirectAfterLoginUrl
                : userUrl
            );
            window.location.reload();
          });
        }
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  return (
    <Formik
      initialValues={{
        email: '',
        password: ''
      }}
      validationSchema={LoginShema}
      onSubmit={values => {
        loginRequest(values);
      }}
    >
      {({ errors, touched, handleSubmit }) => (
        <Form>
          <div id="main">
            <div style={{ width: '350px' }}>
              {isError && (
                <>
                  <Notification variant={{ validation: 'error' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                      <Typography variant={{ bold: true }}>{GENERIC_ERROR_HEADING}</Typography>
                      <Typography>{GENERIC_ERROR_MESSAGE}</Typography>
                    </div>
                  </Notification>
                  <Spacer space={3} />
                </>
              )}
              <div>
                <div id="username_container" style={{ position: 'relative' }}>
                  <div
                    style={{ position: 'relative' }}
                    className={`input-container ${
                      errors.email && touched.email ? 'error' : ''
                    }`}
                  >
                    <Field
                      type="email"
                      name="email"
                      style={{
                        width: '100%',
                        borderColor: '#90909040',
                        height: '45px'
                      }}
                      className="select-style"
                      placeholder={PLACEHOLDER_EMAIL}
                    />

                    <>
                      <div className="error_messages">
                        <span className="frg-body-font-14px">
                          {errors.email}
                        </span>
                      </div>
                      <div
                        id="username_error_icon"
                        className="error_messages_icon"
                      >
                        <StatusError size={16} color="#C12335" aria-hidden="true" />
                      </div>
                    </>
                  </div>
                </div>
                <div style={{ marginTop: 15, position: 'relative' }}>
                  <div
                    style={{ position: 'relative' }}
                    className={`input-container ${
                      errors.password && touched.password ? 'error' : ''
                    }`}
                  >
                    <Field
                      type="password"
                      name="password"
                      style={{
                        width: '100%',
                        borderColor: '#90909040',
                        height: '45px'
                      }}
                      className="select-style"
                      placeholder={USER_LOGIN_PASSWORD}
                    />
                    <>
                      <div className="error_messages">
                        <span className="frg-body-font-14px">
                          {errors.password}
                        </span>
                      </div>
                      <div
                        id="username_error_icon"
                        className="error_messages_icon"
                      >
                        <StatusError size={16} color="#C12335" aria-hidden="true" />
                      </div>
                    </>
                  </div>
                </div>
                {loading && <ValidateLoader message={USER_VERIFY_MESSAGE} />}
                <div style={{ marginTop: 20, textAlign: 'center' }}>
                  <span>
                    <Button 
                      type="submit" 
                      variant={{ priority: 'high', width: 'full' }}
                      style={{ width: '100%' }}
                      onPress={handleSubmit}
                    >
                      Login
                    </Button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

CustomLogin.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default CustomLogin;
